import React from "react";
import { Box, Link, Typography } from "@mui/material";
import flagIcon from "@assets/facilityNetwork/conflict-flag.svg";
import reviewChevron from "@assets/facilityNetwork/conflict-review-chevron-warn.svg";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import type { ParentLinkRequestBannerData } from "./parentLinkRequestUtils";
import { FN } from "./facilityNetworkTheme";

export type { ParentLinkRequestBannerData };

type Props = {
  request: ParentLinkRequestBannerData;
  actionLabel?: "View" | "Review request" | "Review Request";
  onAction: () => void;
};

/** Figma 9227:111920 / AB#10946 — A receives parent link request (warning banner) */
export default function FacilityNetworkParentLinkRequestBanner({
  request,
  actionLabel = "Review Request",
  onAction,
}: Props) {
  const metaParts = [
    `CCN ${request.ccn?.trim() || "NA"}`,
    `NPI ${request.npi?.trim() || "NA"}`,
    request.expiresInDays != null
      ? `Request expires in ${request.expiresInDays} day${request.expiresInDays === 1 ? "" : "s"}`
      : null,
    request.sentToAdmins !== false ? "Sent to all facility admins" : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: "#fff4e5",
        border: "1px solid #fabc89",
        borderRadius: "4px",
        p: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
        <Box sx={{ width: 24, height: 24, flexShrink: 0, display: "flex", alignItems: "center" }}>
          <FacilityNetworkFigmaIcon src={flagIcon} size={24} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: "#8b4409",
              lineHeight: "20px",
            }}
          >
            Parent facility link request from {request.parentFacilityName}
          </Typography>
          {metaParts.length > 0 ? (
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 400,
                color: FN.figmaAccent,
                lineHeight: "20px",
              }}
            >
              {metaParts.join(" · ")}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Link
        component="button"
        type="button"
        underline="none"
        onClick={() => onAction()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
          px: 1.5,
          py: 0.75,
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 400,
          color: "#8b4409",
          lineHeight: "20px",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {actionLabel === "View" ? "View" : "Review Request"}
        <FacilityNetworkFigmaIcon src={reviewChevron} size={12} />
      </Link>
    </Box>
  );
}
